import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChatStore } from '../../hooks/useChatStore';
import { useAuthStore } from '../../hooks/useAuthStore';
import { isDemoMode } from '../../services/demo';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:xn--90aialydaagkmm8g.xn--p1ai:3478' },
    { urls: 'stun:stun.sipnet.ru:3478' },
    {
      urls: ['turn:xn--90aialydaagkmm8g.xn--p1ai:3478?transport=udp', 'turn:xn--90aialydaagkmm8g.xn--p1ai:3478?transport=tcp'],
      username: 'trandi',
      credential: 'yjK18snUftaMtNVYl3GPB2Sl',
    },
    {
      urls: ['turns:xn--90aialydaagkmm8g.xn--p1ai:5349?transport=tcp', 'turn:xn--90aialydaagkmm8g.xn--p1ai:5349?transport=udp'],
      username: 'trandi',
      credential: 'yjK18snUftaMtNVYl3GPB2Sl',
    },
  ],
};

export default function CallManager() {
  const { socket } = useChatStore();
  const { user } = useAuthStore();
  const [callState, setCallState] = useState(null);
  const [callType, setCallType] = useState('video');
  const [remoteUserId, setRemoteUserId] = useState(null);
  const [remoteUsername, setRemoteUsername] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [isGroupCall, setIsGroupCall] = useState(false);
  const [joinedCount, setJoinedCount] = useState(0);
  const callTypeRef = useRef('video');
  const callDurationRef = useRef(0);

  useEffect(() => { callTypeRef.current = callType; }, [callType]);
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);

  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pcMapRef = useRef({});
  const timerRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pendingCandidates = useRef({});
  const pendingOffer = useRef(null);
  const ringtoneRef = useRef(null);
  const callStateRef = useRef(null);
  const groupMembersRef = useRef([]);
  const chatIdRef = useRef('');

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const playRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) ringtoneRef.current = new Audio('/ringtone.wav');
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(() => {});
    } catch (e) {}
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    } catch (e) {}
  }, []);

  const cleanup = useCallback(() => {
    Object.values(pcMapRef.current).forEach((pc) => { try { pc.close(); } catch (e) {} });
    pcMapRef.current = {};
    pendingCandidates.current = {};
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopRingtone();
    setCallState(null);
    setCallDuration(0);
    setRemoteUserId(null);
    setRemoteUsername('');
    setAudioMuted(false);
    setVideoOff(false);
    setIsGroupCall(false);
    setJoinedCount(0);
    groupMembersRef.current = [];
  }, [stopRingtone]);

  const startTimer = useCallback(() => {
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
  }, []);

  useEffect(() => {
    if (localStreamRef.current && localVideoRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [callState, callType]);

  useEffect(() => {
    if (remoteStreamRef.current && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStreamRef.current;
    }
  }, [callState]);

  const getLocalStream = async (video) => {
    return await navigator.mediaDevices.getUserMedia({ audio: true, video });
  };

  const createPeerConnection = useCallback((targetUserId, onConnected) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (e) => {
      if (e.candidate && socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          type: 'ice_candidate',
          targetUserId,
          candidate: {
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
          },
        }));
      }
    };

    pc.ontrack = (e) => {
      if (!remoteStreamRef.current) remoteStreamRef.current = new MediaStream();
      e.streams?.[0]?.getTracks().forEach((t) => {
        if (!remoteStreamRef.current.getTracks().find((x) => x.id === t.id)) {
          remoteStreamRef.current.addTrack(t);
        }
      });
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStreamRef.current;
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === 'connected') {
        onConnected && onConnected(targetUserId);
      } else if (state === 'failed' || state === 'closed') {
        try { pc.close(); } catch (e) {}
        if (pcMapRef.current[targetUserId]) delete pcMapRef.current[targetUserId];
        if (Object.keys(pcMapRef.current).length === 0) {
          cleanup();
        }
      }
    };

    return pc;
  }, [socket, cleanup]);

  useEffect(() => {
    if (!socket) return;

    const handleMessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'call_offer': {
            if (callStateRef.current !== null) {
              if (socket?.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'call_reject', targetUserId: data.fromUserId }));
              }
              break;
            }
            setCallState('incoming');
            setRemoteUserId(data.fromUserId);
            setCallType(data.callType || 'video');
            setRemoteUsername(data.fromUsername || 'Пользователь');
            chatIdRef.current = data.chatId || '';
            pendingOffer.current = data.sdp;
            playRingtone();
            break;
          }
          case 'call_answer': {
            const fromId = data.fromUserId;
            const pc = pcMapRef.current[fromId] || pcMapRef.current[remoteUserId] || Object.values(pcMapRef.current)[0];
            if (pc) {
              await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
              for (const c of (pendingCandidates.current[fromId] || [])) {
                try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {}
              }
              delete pendingCandidates.current[fromId];
              setJoinedCount((n) => n + 1);
              if (callState !== 'connected') {
                startTimer();
                setCallState('connected');
              }
            }
            break;
          }
          case 'call_reject': {
            const fromId = data.fromUserId;
            const pc = pcMapRef.current[fromId];
            if (pc) {
              try { pc.close(); } catch (e) {}
              delete pcMapRef.current[fromId];
            }
            if (data.reason === 'offline') {
              // Оффлайн-участник: в групповом звонке убираем только его;
              // завершаем и показываем сообщение, когда звонок реально закончился.
              const shouldEnd = !callStateRef.current || callStateRef.current === 'incoming'
                || (!isGroupCall || Object.keys(pcMapRef.current).length === 0);
              if (shouldEnd) {
                const wasActive = callStateRef.current != null && callStateRef.current !== 'incoming';
                cleanup();
                if (wasActive) setTimeout(() => alert('Пользователь не в сети'), 0);
              }
              break;
            }
            if (!callStateRef.current || callStateRef.current === 'incoming') {
              cleanup();
            } else if (!isGroupCall || Object.keys(pcMapRef.current).length === 0) {
              cleanup();
            }
            break;
          }
          case 'call_end': {
            const fromId = data.fromUserId;
            const pc = pcMapRef.current[fromId];
            if (pc) {
              try { pc.close(); } catch (e) {}
              delete pcMapRef.current[fromId];
            }
            if (!callStateRef.current || callStateRef.current === 'incoming') {
              cleanup();
            } else if (!isGroupCall || Object.keys(pcMapRef.current).length === 0) {
              cleanup();
            }
            break;
          }
          case 'call_blocked': {
            stopRingtone();
            cleanup();
            setTimeout(() => {
              alert(data.reason === 'self'
                ? 'Вы заблокировали этого пользователя. Разблокируйте его в настройках, чтобы совершать звонки.'
                : 'Данный пользователь добавил ваш аккаунт в блокировку');
            }, 0);
            break;
          }
          case 'ice_candidate': {
            if (data.candidate) {
              const fromId = data.fromUserId;
              const pc = pcMapRef.current[fromId] || pcMapRef.current[remoteUserId];
              if (pc && pc.remoteDescription) {
                try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch (e) {}
              } else {
                if (!pendingCandidates.current[fromId]) pendingCandidates.current[fromId] = [];
                pendingCandidates.current[fromId].push(data.candidate);
              }
            }
            break;
          }
          default: break;
        }
      } catch (e) {
        console.error('Call signal error:', e);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
  }, [socket, cleanup, startTimer, playRingtone, stopRingtone, isGroupCall, callState, remoteUserId]);

  const startCall = async (targetUserId, username, type, groupParticipants, chatId) => {
    if (isDemoMode()) {
      alert('В демо-режиме звонки недоступны');
      return;
    }
    try {
      chatIdRef.current = chatId || '';
      const stream = await getLocalStream(type === 'video');
      localStreamRef.current = stream;
      const isGroup = Array.isArray(groupParticipants) && groupParticipants.length > 1;
      const targets = isGroup
        ? groupParticipants.filter((p) => p.userId !== user?.id)
        : [{ userId: targetUserId }];

      setIsGroupCall(isGroup);
      groupMembersRef.current = targets;
      setRemoteUsername(username);
      setCallType(type);
      setCallState('outgoing');

      for (const t of targets) {
        const pc = createPeerConnection(t.userId, () => {});
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        pcMapRef.current[t.userId] = pc;
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            type: 'call_offer', targetUserId: t.userId,
            chatId: chatIdRef.current || '',
            sdp: { type: offer.type, sdp: offer.sdp },
            callType: type,
            fromUsername: user?.displayName || user?.username || '',
          }));
        }
      }
    } catch (err) {
      console.error('Failed to start call:', err);
      cleanup();
      setTimeout(() => alert('Произошла ошибка при звонке. Попробуйте ещё раз.'), 0);
    }
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      const stream = await getLocalStream(callType === 'video');
      localStreamRef.current = stream;
      const pc = createPeerConnection(remoteUserId, () => {});
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      pcMapRef.current[remoteUserId] = pc;

      if (pendingOffer.current) {
        await pc.setRemoteDescription(new RTCSessionDescription(pendingOffer.current));
      }
      for (const c of (pendingCandidates.current[remoteUserId] || [])) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {}
      }
      delete pendingCandidates.current[remoteUserId];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          type: 'call_answer', targetUserId: remoteUserId,
          sdp: { type: answer.type, sdp: answer.sdp },
        }));
      }

      startTimer();
      setCallState('connected');
      setJoinedCount(1);
      pendingOffer.current = null;
    } catch (err) {
      console.error('Failed to accept call:', err);
      cleanup();
      setTimeout(() => alert('Произошла ошибка при звонке. Попробуйте ещё раз.'), 0);
    }
  };

  const rejectCall = () => {
    postCallRecord('declined');
    if (socket?.readyState === WebSocket.OPEN && remoteUserId) {
      socket.send(JSON.stringify({ type: 'call_reject', targetUserId: remoteUserId }));
    }
    cleanup();
  };

  const postCallRecord = (status) => {
    try {
      const target = remoteUserId || groupMembersRef.current[0]?.userId || '';
      fetch('/api/calls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: chatIdRef.current || '',
          targetUserId: target,
          callType: callTypeRef.current,
          status,
          duration: String(callDurationRef.current * 1000),
        }),
      }).catch(() => {});
    } catch (e) {}
  };

  const endCall = () => {
    stopRingtone();
    const st = callStateRef.current;
    if (st === 'connected') postCallRecord('completed');
    else if (st === 'outgoing') postCallRecord('missed');
    const targets = Object.keys(pcMapRef.current);
    if (socket?.readyState === WebSocket.OPEN) {
      targets.forEach((uid) => {
        socket.send(JSON.stringify({ type: 'call_end', targetUserId: uid }));
      });
    }
    cleanup();
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = audioMuted; });
      setAudioMuted(!audioMuted);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = videoOff; });
      setVideoOff(!videoOff);
    }
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => () => cleanup(), [cleanup]);

  useEffect(() => {
    window.startCall = startCall;
    return () => { delete window.startCall; };
  }, [startCall]);

  useEffect(() => {
    const onError = (event) => {
      try {
        fetch('/api/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: (event?.error?.stack || event?.message || 'unknown').slice(0, 2000),
            url: window.location.href.slice(0, 300),
            source: 'call',
          }),
        }).catch(() => {});
      } catch (e) {}
    };
    const onRejection = (event) => {
      try {
        const err = event?.reason;
        fetch('/api/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: ((err && (err.stack || err.message)) || 'unhandled rejection').slice(0, 2000),
            url: window.location.href.slice(0, 300),
            source: 'call-promise',
          }),
        }).catch(() => {});
      } catch (e) {}
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (!callState) return null;

  const groupLabel = isGroupCall ? ` · в разговоре: ${joinedCount}/${groupMembersRef.current.length}` : '';

  return (
    <div className="call-overlay">
      <div className="call-modal">
        {callState === 'incoming' && (
          <>
            <div className="call-avatar">{callType === 'video' ? '📹' : '📞'}</div>
            <h2 style={{ color: 'white', margin: '12px 0 4px' }}>{remoteUsername}</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>
              Входящий {callType === 'video' ? 'видео' : 'аудио'}вызов...
            </p>
            <div className="call-actions">
              <button className="call-btn reject" onClick={rejectCall}>✕</button>
              <button className="call-btn accept" onClick={acceptCall}>📞</button>
            </div>
          </>
        )}
        {callState === 'outgoing' && (
          <>
            <div className="call-avatar pulse">{callType === 'video' ? '📹' : '📞'}</div>
            <h2 style={{ color: 'white', margin: '12px 0 4px' }}>{remoteUsername}</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>
              {isGroupCall ? `Вызов группы... (${joinedCount}/${groupMembersRef.current.length})` : 'Вызов...'}
            </p>
            <div className="call-actions">
              <button className="call-btn reject" onClick={endCall}>✕</button>
            </div>
          </>
        )}
        {callState === 'connected' && (
          <>
            <div className="call-videos">
              <video ref={remoteVideoRef} autoPlay playsInline className="remote-video" style={{ display: callType === 'video' ? 'block' : 'none' }} />
              <video ref={localVideoRef} autoPlay playsInline muted className="local-video" style={{ display: callType === 'video' ? 'block' : 'none' }} />
            </div>
            <div className="call-info">
              <span className="call-timer">{formatDuration(callDuration)}</span>
              <span style={{ color: 'white', marginLeft: 8 }}>{remoteUsername}{groupLabel}</span>
            </div>
            <div className="call-actions connected">
              <button className={`call-control-btn ${audioMuted ? 'off' : ''}`} onClick={toggleAudio}>
                {audioMuted ? '🔇' : '🎤'}
              </button>
              {callType === 'video' && (
                <button className={`call-control-btn ${videoOff ? 'off' : ''}`} onClick={toggleVideo}>
                  {videoOff ? '📷' : '📹'}
                </button>
              )}
              <button className="call-btn reject" onClick={endCall}>✕</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
